import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { CallIntelTypes, CallIntelUtils } from '@frontend/api-call-intel';
import { http } from '@frontend/fetch';
import { validateUUIDV4 } from '@frontend/string';

dayjs.extend(utc);
dayjs.extend(timezone);

const baseUrlV2 = 'call-intelligence/v2';

const generateCommonFilterPayload = (filters: CallIntelTypes.Filters): CallIntelTypes.FilterPayload => {
  // Get the current timezone offset in minutes
  const timezoneOffsetMinutes = dayjs().utcOffset();
  const start = dayjs(filters.startDate)
    .startOf('day')
    .add(-timezoneOffsetMinutes, 'minute')
    .format('YYYY-MM-DD HH:mm:ss.SSS');

  const end = dayjs(filters.endDate)
    .endOf('day')
    .add(-timezoneOffsetMinutes, 'minute')
    .format('YYYY-MM-DD HH:mm:ss.SSS');

  return {
    filter: {
      [CallIntelTypes.FilterTypeEnum.FILTER_BY_DATE_RANGE]: {
        date_range: {
          end,
          start,
        },
      },
      [CallIntelTypes.FilterTypeEnum.FILTER_BY_LOCATION]: {
        locations: filters.locations,
      },
    },
  };
};

const generateFollowupsFilterPayload = (filters: CallIntelTypes.FollowupTaskTypeFilters) => {
  const { officeUsers, officeHours, contactTypes, taskTypes, taskStatus } = filters;
  const payload = generateCommonFilterPayload(filters);

  if (officeUsers?.length) {
    payload.filter[CallIntelTypes.FollowupFilterTypeIndexEnum.FOLLOWUPS_FILTER_BY_OFFICE_USER] = {
      office_user_id: officeUsers,
    };
  }

  if (contactTypes?.length) {
    payload.filter[CallIntelTypes.FollowupFilterTypeIndexEnum.FOLLOWUPS_FILTER_BY_CONTACT_TYPE] = {
      contact_type: contactTypes,
    };
  }

  if (taskTypes?.length) {
    payload.filter[CallIntelTypes.FollowupFilterTypeIndexEnum.FOLLOWUPS_FILTER_BY_TASK_TYPE] = {
      task_type: taskTypes,
    };
  }

  if (taskStatus?.length) {
    payload.filter[CallIntelTypes.FollowupFilterTypeIndexEnum.FOLLOWUPS_FILTER_BY_TASK_STATUS] = {
      task_status: taskStatus,
    };
  }

  if (officeHours?.length) {
    payload.filter[CallIntelTypes.FollowupFilterTypeIndexEnum.FOLLOWUPS_FILTER_BY_OFFICE_HOURS] = {
      office_hours: officeHours,
    };
  }

  return payload;
};

const generateChartsPayload = ({ drillDownOptions, filters }: CallIntelTypes.ApiPayloadProps) => {
  const payload = generateCommonFilterPayload(filters);

  if (filters.contactTypes?.length) {
    payload.filter[CallIntelTypes.FilterTypeEnum.FILTER_BY_CONTACT_TYPE] = {
      contact_type: filters.contactTypes,
    };
  }

  payload.filter = {
    ...payload.filter,
    [CallIntelTypes.FilterTypeEnum.FILTER_BY_CALL_STATUS]: {
      call_status: [CallIntelTypes.CallStatusEnum.CALL_STATUS_COMPLETE],
    },
  };

  if (drillDownOptions) {
    payload.filter = {
      ...payload.filter,
      ...CallIntelUtils.generateDrillDownPayload(drillDownOptions),
    };
  }

  return payload;
};

export const downloadCallRecording = (locationId: string, callId: string, isDemoAccount?: boolean): Promise<Blob> =>
  http.get<Blob>(
    isDemoAccount ? `${baseUrlV2}/demo/call-recording` : `${baseUrlV2}/calls/${locationId}/${callId}/recording`,
    {
      responseType: 'blob',
    }
  );

export const getDemoServiceQualityChartStats = async ({ includes, filters }: CallIntelTypes.ApiOverviewStatsParams) => {
  if (!filters.locations?.length || !filters.startDate || !filters.endDate) {
    return;
  }

  const { filter: chartsFilter } = generateChartsPayload({ filters });

  const payload = {
    request: {
      filter: {
        ...chartsFilter,
        [CallIntelTypes.FilterTypeEnum.FILTER_BY_LOCATION]: {
          locations: ['40417eee-943e-4b57-80d8-794b41676885'], // This is hardcoded location id for demodata
        },
      },
      includes: {
        opportunities: includes.opportunities,
        service_quality: includes.serviceQuality,
      },
      timezone: dayjs.tz.guess(),
    },
  };

  return http.post(`${baseUrlV2}/demo/calls/service-quality`, payload);
};

export const getAllStats = ({ filters }: CallIntelTypes.GetFollowUpsStatsParams) => {
  if (!filters.locations?.length || !filters.startDate || !filters.endDate) {
    return;
  }
  const payload = {
    ...generateFollowupsFilterPayload(filters),
  };
  return http.post<CallIntelTypes.GetCallFollowupsStatsResponse>(`${baseUrlV2}/calls/followups/stats`, payload);
};

export const getFollowUps = async ({
  filters,
  pageConfig,
  sortBy,
  sortType,
  searchQuery,
}: CallIntelTypes.GetFollowUpsParams) => {
  if (!filters.locations?.length || !filters.startDate || !filters.endDate) {
    return {
      followUps: [],
      hasNextPage: false,
      pageConfig,
      totalFollowUps: 0,
    };
  }

  let isUUID = false;

  if (searchQuery) {
    isUUID = validateUUIDV4(searchQuery);
  }

  const payload = {
    ...generateFollowupsFilterPayload(filters),
    limit: pageConfig.pageSize + 1,
    offset: (pageConfig.pageNumber - 1) * pageConfig.pageSize,
    sort_by: sortBy,
    sort_type: sortType ?? CallIntelTypes.SortTypeIndexEnum.SORT_DESC,
    ...(searchQuery && !isUUID && { search_query: CallIntelUtils.generateSearchPayload(searchQuery) }),
  };

  const data = await http.post<CallIntelTypes.ListFollowupsResponse>(`${baseUrlV2}/calls/followups`, payload);

  return {
    followUps: (data.followUps ?? []).slice(0, pageConfig.pageSize),
    hasNextPage: (data.followUps ?? []).length > pageConfig.pageSize,
    hasPrivateCalls: data.hasPrivateCalls,
    pageConfig,
    totalFollowUps: data.totalFollowUps ?? 0,
  };
};

export const getCallsStats = ({ filters, searchQuery }: CallIntelTypes.GetCallStatsParams) => {
  if (!filters?.locations?.length || !filters.startDate || !filters.endDate) {
    return;
  }

  const search = searchQuery?.search ?? '';
  const searchType = searchQuery?.searchType;
  const isUUIDSearch = !!search && validateUUIDV4(search);

  const basePayload = CallIntelUtils.generateCallsFilterPayload({ filters });

  const filter = {
    ...basePayload.filter,
    [CallIntelTypes.FilterTypeEnum.FILTER_BY_CALL_STATUS]: {
      callStatus: [
        CallIntelTypes.CallStatusEnum.CALL_STATUS_ERROR,
        CallIntelTypes.CallStatusEnum.CALL_STATUS_FAILED,
        CallIntelTypes.CallStatusEnum.CALL_STATUS_SKIPPED,
      ],
    },
    ...(isUUIDSearch && {
      [CallIntelTypes.FilterTypeEnum.FILTER_BY_CALL_ID]: {
        callId: [search],
      },
    }),
  };

  const payload = {
    filter,
    ...(search &&
      searchType &&
      !isUUIDSearch && {
        searchQuery: {
          search,
          searchType,
        },
      }),
  };

  return http.post<CallIntelTypes.GetCallsCountResponse>(`${baseUrlV2}/calls/stats`, payload);
};

export const noopMutationFn = async (value: any) => Promise.resolve(value);

export const fakeApi = async <T>(value: T, delay = 200): Promise<T> => {
  return new Promise((resolve) => {
    setTimeout(() => resolve(value), delay);
  });
};
